import { createContext, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orderService } from '../../../shared/services/orders/orderService';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth';

export interface Order {
  id: string;
  orderCode: string;
  receivedAt: string;
  origin: string;
  recipientName: string;
  receivedBy: string;
  deliveredBy: string;
  deliveredAt: string;
  deliveryDocument: string;
  user?: {
    name: string;
  };
  pickupToken: string;
  pickupPin: string;
  status: string;
}

interface OrderContextData {
  orders: Order[];
  isLoading: boolean;
  error: string | null;
  setError: (error: string | null) => void;
  handleCreate: () => void;
  handleView: (order: Order) => void;
  handleCloseError: () => void;
  setOrderId: (id: string) => void;
  // Modal de Delete
  isDeleteModalOpen: boolean;
  selectedOrder: Order | null;
  openDeleteModal: (order: Order) => void;
  closeDeleteModal: () => void;
  handleDeleteOrder: () => void;
  isDeleting: boolean;

  // Navegação
  navigateToEdit: (id: string) => void;

  // Form
  orderData: Order | null;
  isDataLoading: boolean;
  createOrder: (data: Omit<Order, 'id' | 'pickupToken' | 'pickupPin'>) => Promise<void>;
  deliverOrder: (id: string, data: { deliveredBy: string; deliveredAt: string; deliveryDocument: string }) => Promise<void>;
  updateOrder: (id: string, data: any) => Promise<void>;
  isSubmitting: boolean;
}

interface OrderProviderProps {
  children: ReactNode;
}

export const OrderContext = createContext({} as OrderContextData);

export function OrderProvider({ children }: OrderProviderProps) {
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['orders', auth.selectedTenant],
    queryFn: async () => {
      if (!auth.selectedTenant) return [];
      try {

        const isCustomer = auth.user?.role?.includes('customer');
        let response;
        if (isCustomer) {
          response = await orderService.listByUser(auth.user?.id);
        } else {
          response = await orderService.listByTenant(auth.selectedTenant.id);
        }

        return response.data;
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error.message || 'Erro ao buscar encomendas';
        setError(errorMessage);
        return [];
      }
    },
  });


  // Query para buscar dados do order no modo de edição/detalhe
  const [orderId, setOrderId] = useState<string | null>(null);
  const { data: orderData, isLoading: isDataLoading } = useQuery({
    queryKey: ['order', orderId],
    queryFn: async () => {
      if (!orderId) return null;
      try {
        const response = await orderService.get(orderId);
        return response.data;
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error.message || 'Erro ao buscar encomenda';
        setError(errorMessage);
        return null;
      }
    },
    enabled: !!orderId,
  });

  // Estado do modal de delete
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  // Mutation para deletar order
  const deleteMutation = useMutation({
    mutationFn: (id: string) => orderService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', auth.selectedTenant] });
      navigate('/orders');
    }
  });

  // Handlers para listagem
  const handleCreate = () => {
    navigate('/orders/new');
  };

  const handleView = (order: Order) => {
    setOrderId(order.id);
    navigate(`/orders/${order.id}`);
  };

  const handleCloseError = () => {
    setError(null);
  };

  // Handlers para modal de delete
  const openDeleteModal = (order: Order) => {
    setSelectedOrder(order);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedOrder(null);
  };

  const handleDeleteOrder = () => {
    if (selectedOrder?.id) {
      deleteMutation.mutate(selectedOrder.id);
    }
  };

  // Handler para navegação
  const navigateToEdit = (id: string) => {
    navigate(`/orders/${id}/edit`);
  };

  // Mutations para criar, entregar e atualizar orders
  const createMutation = useMutation({
    mutationFn: (data: Omit<Order, 'id' | 'pickupToken' | 'pickupPin'>) => orderService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', auth.selectedTenant] });
      navigate('/orders');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error.message || 'Erro ao criar encomenda';
      setError(errorMessage);
    }
  });

  const deliverMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { deliveredBy: string; deliveredAt: string; deliveryDocument: string } }) =>
      orderService.deliver(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', auth.selectedTenant] });
      if (orderId) queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      navigate('/orders');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error.message || 'Erro ao entregar encomenda';
      setError(errorMessage);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => orderService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', auth.selectedTenant] });
      if (orderId) queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      navigate('/orders');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error.message || 'Erro ao atualizar encomenda';
      setError(errorMessage);
    }
  });

  const createOrder = async (data: Omit<Order, 'id' | 'pickupToken' | 'pickupPin'>) => {
    await createMutation.mutateAsync(data);
  };

  const deliverOrder = async (id: string, data: { deliveredBy: string; deliveredAt: string; deliveryDocument: string }) => {
    await deliverMutation.mutateAsync({ id, data });
  };

  const updateOrder = async (id: string, data: any) => {
    await updateMutation.mutateAsync({ id, data });
  };

  return (
    <OrderContext.Provider
      value={{
        orders,
        isLoading,
        error,
        setError,
        handleCreate,
        handleView,
        handleCloseError,
        setOrderId,

        // Modal de Delete
        isDeleteModalOpen,
        selectedOrder,
        openDeleteModal,
        closeDeleteModal,
        handleDeleteOrder,
        isDeleting: deleteMutation.isPending,

        // Navegação
        navigateToEdit,

        // Form
        orderData,
        isDataLoading,
        createOrder,
        deliverOrder,
        updateOrder,
        isSubmitting: createMutation.isPending || deliverMutation.isPending,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
}